import { IDeliveryRelation, IInvoiceItemDraft, IOrderRelation } from './IInvoiceDraftDto';
import { UUID } from '@lib/util';
import { IMoney } from '../../types/IMoney';
import {
    Accounting,
    AccountingChange,
    AccountingSelectionInput,
    AccountingType,
    CustomAccountingInput,
    IAccounting,
} from '@lib/accounting/types';

export class InvoiceItem {
    /**
     * Internal ID for the item. Will not be sent to the backend.
     * This ID is used to identify the item in the UI.
     */
    id: string;
    /**
     * Persisted invoice-item id from the backend, when the draft was loaded from a stored invoice.
     * Undefined for items that only exist client-side. Used to address the item for manual order
     * linking (EPROC-12024).
     */
    persistedId?: string;
    supplierArticleNumber: string;
    name: string;
    description: string;
    productUnit: string;
    contentUnitsPerOrderUnit: number;
    contentUnit: string;
    unitPrice: number;
    quantity: number;
    discountPercentage: number;
    taxInPercent: number;
    priceQuantity: number;
    materialGroupCode?: string | null;
    deliveryRelation?: IDeliveryRelation;
    /** Manual order link; travels with the save so it survives it. */
    orderRelation?: IOrderRelation | null;
    lineTotal?: IMoney;
    accountingInput: AccountingSelectionInput;
    accountingChanges: AccountingChange[];

    constructor(dto: IInvoiceItemDraft) {
        this.id = UUID.generate(); // create a temp ID for internal usage
        this.persistedId = dto.id; // backend id, present only when loaded from a stored invoice
        this.supplierArticleNumber = dto.supplierArticleNumber ?? '';
        this.name = dto.name ?? '';
        this.description = dto.description ?? '';
        this.productUnit = dto.quantity.unit ?? 'C62';
        this.contentUnitsPerOrderUnit = dto.contentUnitsPerOrderUnit ?? 1;
        this.contentUnit = dto.contentUnit ?? 'C62';
        this.unitPrice = dto.price.amount ?? 0;
        this.quantity = dto.quantity.amount ?? 1;
        this.discountPercentage = dto.discountPercentage ?? 0;
        this.taxInPercent = dto.taxInPercent ?? 19;
        this.priceQuantity = dto.priceQuantity ?? 1;
        this.materialGroupCode = dto.materialGroupCode ?? null;
        this.lineTotal = dto.lineTotal;
        if (dto.deliveryRelation) {
            this.deliveryRelation = dto.deliveryRelation;
        }
        this.orderRelation = dto.orderRelation;
        this.accountingInput = new AccountingSelectionInput();
        this.accountingChanges = [];
        const pushBaseAccounting = (acc: IAccounting, type: AccountingType) => {
            this.accountingChanges.push(
                new AccountingChange(type, new Accounting(+acc.id, acc.key, acc.name, acc.description)),
            );
        };
        if (dto.changeAccountingData.length > 0) {
            for (const accounting of dto.changeAccountingData) {
                switch (accounting.type) {
                    case AccountingType.COST_CENTER:
                        this.accountingInput = this.accountingInput.setCostCenter(
                            Accounting.fromAccounting(accounting._value),
                        );
                        pushBaseAccounting(accounting._value, accounting.type);
                        break;
                    case AccountingType.PROJECT:
                        this.accountingInput = this.accountingInput.setProject(
                            Accounting.fromAccounting(accounting._value),
                        );
                        pushBaseAccounting(accounting._value, accounting.type);
                        break;
                    case AccountingType.GENERAL_LEDGER_ACCOUNT:
                        this.accountingInput = this.accountingInput.setGeneralLedgerAccount(
                            Accounting.fromAccounting(accounting._value),
                        );
                        pushBaseAccounting(accounting._value, accounting.type);
                        break;
                    case AccountingType.CUSTOM:
                        const acc: IAccounting = accounting._value;
                        this.accountingInput.customAccountAssignments.push(
                            new CustomAccountingInput(accounting.customAccountingTypeId, acc.key),
                        );
                        this.accountingChanges.push(
                            new AccountingChange(
                                accounting.type,
                                new Accounting(+acc.id, acc.key, acc.name, acc.description),
                                accounting.customAccountingTypeId,
                            ),
                        );
                        break;
                }
            }
        }
    }
}
